// ---- Types ------------------------------------------------------------------

export interface FluencyScoreBasic {
  syntactic_maturity: number;
  textual_cohesion: number;
  lexical_precision: number;
  summary: string;
}

export interface SavedAssessment {
  id: string;
  child_name: string;
  year_group: string;
  class_name?: string;
  key_stage_name?: string;
  grade: string;
  assessment_text: string;
  term: string | null;
  created_at: string;
  assessed_by: string;
  fluency_score?: FluencyScoreBasic | null;
}

// ---- Grade helpers ----------------------------------------------------------

const GRADE_MAP: Record<string, string> = {
  working_towards: 'Working Towards',
  expected: 'Expected',
  greater_depth: 'Greater Depth',
  WTS: 'Working Towards',
  ES: 'Expected',
  GD: 'Greater Depth',
};

const GRADE_NORMALISE: Record<string, string> = {
  working_towards: 'working_towards',
  expected: 'expected',
  greater_depth: 'greater_depth',
  WTS: 'working_towards',
  ES: 'expected',
  GD: 'greater_depth',
};

/** Normalises any grade representation to the internal DB enum format. */
export function normaliseGrade(raw: string | null | undefined): string {
  if (!raw) return '';
  return GRADE_NORMALISE[raw] ?? raw;
}

/** Derives a UK school term label from an ISO date string. */
export function getTermFromDate(dateStr: string): string {
  const date = new Date(dateStr);
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  if (month >= 9) return `Autumn ${year}`;
  if (month <= 3) return `Spring ${year}`;
  return `Summer ${year}`;
}

const GRADE_ORDER = ['Working Towards', 'Expected', 'Greater Depth'];

/**
 * Returns a fixed-order array of { grade, count } for the recharts BarChart.
 * Always returns all three grades even if count is 0.
 */
export function getGradeDistribution(
  assessments: SavedAssessment[],
): { grade: string; count: number }[] {
  const counts: Record<string, number> = {
    'Working Towards': 0,
    Expected: 0,
    'Greater Depth': 0,
  };
  for (const a of assessments) {
    const label = GRADE_MAP[a.grade];
    if (label) counts[label]++;
  }
  return GRADE_ORDER.map((grade) => ({ grade, count: counts[grade] }));
}

/**
 * Groups assessments by child_name using strict string equality.
 */
export function groupByChildName(
  assessments: SavedAssessment[],
): Record<string, SavedAssessment[]> {
  return assessments.reduce<Record<string, SavedAssessment[]>>((acc, a) => {
    if (!acc[a.child_name]) acc[a.child_name] = [];
    acc[a.child_name].push(a);
    return acc;
  }, {});
}

/**
 * Returns distinct non-null term values sorted by the most recent
 * created_at in each term group (most recently active term first).
 */
export function sortTermsByRecency(assessments: SavedAssessment[]): string[] {
  const latestByTerm = new Map<string, string>();
  for (const a of assessments) {
    if (!a.term) continue;
    const existing = latestByTerm.get(a.term);
    if (!existing || a.created_at > existing) {
      latestByTerm.set(a.term, a.created_at);
    }
  }
  return [...latestByTerm.entries()]
    .sort((a, b) => (b[1] > a[1] ? 1 : -1))
    .map(([term]) => term);
}

/**
 * Returns the assessment with the latest created_at, or undefined for an empty array.
 */
export function getLatestAssessment(
  assessments: SavedAssessment[],
): SavedAssessment | undefined {
  if (assessments.length === 0) return undefined;
  return assessments.reduce((latest, a) =>
    a.created_at > latest.created_at ? a : latest,
  );
}

// ---- Chart data helpers -----------------------------------------------------

function formatMonth(yyyyMM: string): string {
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const [y, m] = yyyyMM.split('-');
  return `${MONTHS[parseInt(m, 10) - 1]} '${y.slice(2)}`;
}

export interface AvgGradeTrendPoint {
  month: string;
  avg: number; // numeric average: 1=WTS, 2=ES, 3=GD
}

/**
 * Returns average numeric grade per calendar month.
 * Assessments without a recognised grade are skipped.
 * Used for the "Average Grade Over Time" single-line chart.
 * Works for both cohort view (true averages) and single-pupil view (their monthly grade).
 */
export function getAvgGradeTrend(assessments: SavedAssessment[]): AvgGradeTrendPoint[] {
  const GRADE_NUM_MAP: Record<string, number> = {
    working_towards: 1,
    expected: 2,
    greater_depth: 3,
  };
  const byMonth = new Map<string, number[]>();
  for (const a of assessments) {
    const g = normaliseGrade(a.grade);
    const num = GRADE_NUM_MAP[g];
    if (num === undefined) continue;
    const month = a.created_at.slice(0, 7);
    if (!byMonth.has(month)) byMonth.set(month, []);
    byMonth.get(month)!.push(num);
  }
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([yyyyMM, vals]) => ({
      month: formatMonth(yyyyMM),
      avg: Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 10) / 10,
    }));
}

export interface GradeScatterPoint {
  x: number;    // timestamp ms — recharts XAxis type="number"
  y: number;    // 1 = WTS, 2 = ES, 3 = GD
  name: string; // pupil display name
}

const GRADE_NUM: Record<string, number> = {
  working_towards: 1,
  expected: 2,
  greater_depth: 3,
};

/**
 * Returns scatter plot data split into three series by grade.
 * Used with ScatterChart + three <Scatter> components (one per grade).
 */
export function getGradeScatterSeries(assessments: SavedAssessment[]): {
  label: string;
  data: GradeScatterPoint[];
}[] {
  return [
    { label: 'Working Towards', grade: 'working_towards', y: 1 },
    { label: 'Expected', grade: 'expected', y: 2 },
    { label: 'Greater Depth', grade: 'greater_depth', y: 3 },
  ].map(({ label, grade, y }) => ({
    label,
    data: assessments
      .filter((a) => normaliseGrade(a.grade) === grade)
      .map((a) => ({
        x: new Date(a.created_at).getTime(),
        y,
        name: a.child_name,
      })),
  }));
}

export interface FluencyTrendPoint {
  month: string;
  'Syntactic Maturity': number | null;
  'Textual Cohesion': number | null;
  'Lexical Precision': number | null;
}

/**
 * Returns average fluency scores grouped by calendar month.
 * Rows without fluency data are skipped; months with no data produce nulls
 * (recharts Line renders a gap by default).
 */
export function getFluencyTrend(assessments: SavedAssessment[]): FluencyTrendPoint[] {
  const byMonth = new Map<string, { sm: number[]; tc: number[]; lp: number[] }>();
  for (const a of assessments) {
    const fs = a.fluency_score;
    if (!fs) continue;
    const month = a.created_at.slice(0, 7);
    if (!byMonth.has(month)) byMonth.set(month, { sm: [], tc: [], lp: [] });
    const b = byMonth.get(month)!;
    b.sm.push(fs.syntactic_maturity);
    b.tc.push(fs.textual_cohesion);
    b.lp.push(fs.lexical_precision);
  }
  const avg = (arr: number[]): number | null =>
    arr.length === 0 ? null : Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10;
  return Array.from(byMonth.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([yyyyMM, b]) => ({
      month: formatMonth(yyyyMM),
      'Syntactic Maturity': avg(b.sm),
      'Textual Cohesion': avg(b.tc),
      'Lexical Precision': avg(b.lp),
    }));
}

/**
 * Returns average fluency scores for the entire filtered set.
 * Returns an empty array if no assessments have fluency data.
 */
export function getFluencyAverages(
  assessments: SavedAssessment[],
): { metric: string; value: number }[] {
  let smSum = 0, tcSum = 0, lpSum = 0, count = 0;
  for (const a of assessments) {
    const fs = a.fluency_score;
    if (!fs) continue;
    smSum += fs.syntactic_maturity;
    tcSum += fs.textual_cohesion;
    lpSum += fs.lexical_precision;
    count++;
  }
  if (count === 0) return [];
  const r = (n: number) => Math.round((n / count) * 10) / 10;
  return [
    { metric: 'Syntactic Maturity', value: r(smSum) },
    { metric: 'Textual Cohesion', value: r(tcSum) },
    { metric: 'Lexical Precision', value: r(lpSum) },
  ];
}

/** Grade numeric value for scatter Y axis label formatting. */
export const GRADE_NUM_LABEL: Record<number, string> = { 1: 'WTS', 2: 'ES', 3: 'GD' };

// ---- Overall grade helpers --------------------------------------------------

const GRADE_NUM_MAP: Record<string, number> = {
  working_towards: 1,
  expected: 2,
  greater_depth: 3,
};

/**
 * Maps a numeric average (1–3) back to a grade enum.
 * < 1.5 => working_towards, < 2.5 => expected, >= 2.5 => greater_depth
 */
export function avgToGrade(
  avg: number,
): 'working_towards' | 'expected' | 'greater_depth' {
  if (avg < 1.5) return 'working_towards';
  if (avg < 2.5) return 'expected';
  return 'greater_depth';
}

/**
 * Returns an overall grade for a set of assessments by averaging numeric values.
 * Returns null if no assessments have a recognised grade.
 */
export function getOverallGrade(
  assessments: SavedAssessment[],
): 'working_towards' | 'expected' | 'greater_depth' | null {
  const nums = assessments
    .map((a) => GRADE_NUM_MAP[normaliseGrade(a.grade)])
    .filter((n): n is number => n !== undefined);
  if (nums.length === 0) return null;
  const avg = nums.reduce((s, v) => s + v, 0) / nums.length;
  return avgToGrade(avg);
}
